// API configuration for backend connections
const API_CONFIG = {
  // Use the correct backend Railway URL
  // Try to use the .env variable first, fallback to local Flask server automatically
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
  // API endpoints
  endpoints: {
    artists: '/api/artists',
    heatmap: '/api/heatmap',
    genreVotes: '/api/genre-votes',
    updateNow: '/api/update-now',
    artistTopTrack: '/api/artist-top-track',
    artistSample: '/api/artist-sample',
    fanProfile: '/api/fan-profile',
    artistSavedArtists: '/api/artist-saved-artists',
    topFans: '/api/top-fans',
    hallOfFame: '/api/hall-of-fame',
    alreadyMainstream: '/api/already-mainstream',
    veterans: '/api/veterans',
    compareArtists: '/api/compare-artists',
    badgeConfig: '/api/admin/badge-config',
    cosign: '/api/cosign',
    cosignNote: '/api/cosign/note',
    cosignRemove: '/api/cosign/remove',
    scoutWallet: '/api/scout-wallet',
    livery: '/api/livery',
    liveryUnlock: '/api/livery/unlock',
    connections: '/api/connections',
    economy: '/api/admin/economy'
  },

  // Request configuration
  defaultHeaders: {
    'Content-Type': 'application/json',
  },

  // Request timeout (15 seconds). Fetch ignores a timeout option; the helpers
  // below abort instead.
  timeout: 15000
};

function timeoutError() {
  const error = new Error('Request timed out');
  error.name = 'TimeoutError';
  return error;
}

// Rejects when `promise` is still pending after `ms`. Use this for Firestore
// reads and getIdToken(), which have no abort handle. Does not cancel the
// underlying work.
export function withTimeout(promise, ms = API_CONFIG.timeout) {
  let timerId;
  const timeout = new Promise((_, reject) => {
    timerId = setTimeout(() => reject(timeoutError()), ms);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timerId));
}

// Runs `work(signal)` and aborts that signal at the deadline. The timer stays
// armed until `work` settles, so a response.json() inside `work` counts
// against the same 15 seconds. A caller abort is not relabeled as a timeout.
export async function runWithTimeout(work, { timeout: timeoutOption, signal: callerSignal } = {}) {
  const timeoutMs = Number.isFinite(timeoutOption) && timeoutOption > 0
    ? timeoutOption
    : API_CONFIG.timeout;

  const controller = new AbortController();
  let timedOut = false;
  let detachCaller = () => {};

  if (callerSignal && callerSignal.aborted) {
    const reason = callerSignal.reason;
    if (reason instanceof Error) throw reason;
    const aborted = new Error(typeof reason === 'string' && reason ? reason : 'The operation was aborted.');
    aborted.name = 'AbortError';
    throw aborted;
  }

  let signal = controller.signal;
  if (callerSignal && typeof AbortSignal.any === 'function') {
    signal = AbortSignal.any([controller.signal, callerSignal]);
  } else if (callerSignal) {
    const onCallerAbort = () => controller.abort(callerSignal.reason);
    callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    detachCaller = () => callerSignal.removeEventListener('abort', onCallerAbort);
  }

  // Reject from the timer itself. Aborting the signal does not stop work that
  // never listens to it (getIdToken, a body read that already started).
  let timerId;
  try {
    return await new Promise((resolve, reject) => {
      timerId = setTimeout(() => {
        timedOut = true;
        controller.abort(timeoutError());
        reject(timeoutError());
      }, timeoutMs);
      Promise.resolve()
        .then(() => work(signal))
        .then(resolve, (error) => reject(timedOut ? timeoutError() : error));
    });
  } catch (error) {
    if (timedOut) throw timeoutError();
    throw error;
  } finally {
    clearTimeout(timerId);
    detachCaller();
  }
}

// Drop-in for fetch() that aborts at the deadline. The body read is only
// covered if it happens inside `runWithTimeout` — prefer that when you
// immediately parse JSON.
export function fetchWithTimeout(url, options = {}) {
  const { timeout, signal, ...rest } = options;
  return runWithTimeout(
    (abortSignal) => fetch(url, { ...rest, signal: abortSignal }),
    { timeout, signal }
  );
}

// Helper function to make API calls
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.baseURL}${endpoint}`;
  const {
    timeout: timeoutOption,
    signal: callerSignal,
    headers: optionHeaders,
    ...fetchOptions
  } = options;

  try {
    if (process.env.NODE_ENV !== 'production') console.log(`Making API call to: ${url}`);
    const data = await runWithTimeout(async (signal) => {
      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          ...API_CONFIG.defaultHeaders,
          ...optionHeaders
        },
        signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    }, { timeout: timeoutOption, signal: callerSignal });
    // Response payloads here can be the full 1000-5000-artist roster - not
    // worth serializing/logging on every call even in dev, and definitely
    // not shipped unconditionally to production like this previously was.
    if (process.env.NODE_ENV !== 'production') console.log(`API call successful:`, data);
    return data;
  } catch (error) {
    console.error(`API call failed for ${url}:`, error);
    throw error;
  }
};

// getArtists(1000)/getArtists(5000) fetch the whole roster (with each
// artist's full downsampled history) and are called independently by
// ArtistsTable, ArtistPage, HeatMap, and GenreDiscovery - navigating between
// them used to re-fetch and re-parse the same multi-MB payload from scratch
// every time, seconds apart. Cached per distinct `limit` for a short TTL
// (the underlying data only actually changes once/day via the collection
// cron job, so this is purely about not re-fetching within one browsing
// session), with in-flight de-duplication so two components mounting near-
// simultaneously share a single request instead of firing two.
const ARTISTS_CACHE_TTL_MS = 2 * 60 * 1000;
const _artistsCache = new Map(); // limit -> { data, at }
const _artistsInFlight = new Map(); // limit -> Promise
// Set by invalidateArtistCaches(): the next fetch must also skip the
// browser's HTTP cache, since both endpoints send Cache-Control max-age=300.
const _artistsBypassHttpCache = new Set(); // limits
let _heatmapBypassHttpCache = false;

// Drop the roster/heatmap caches after an admin mutation (e.g. deleting an
// artist) so the change shows on the next board/map visit in this tab
// instead of after the TTL. Other users still see it once their caches expire.
export const invalidateArtistCaches = () => {
  for (const limit of _artistsCache.keys()) _artistsBypassHttpCache.add(limit);
  _artistsCache.clear();
  _heatmapCache = null;
  _heatmapBypassHttpCache = true;
};

// Specific API functions
export const getArtists = (limit = 20) => {
  const cached = _artistsCache.get(limit);
  if (cached && (Date.now() - cached.at) < ARTISTS_CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  const inFlight = _artistsInFlight.get(limit);
  if (inFlight) return inFlight;

  const bypass = _artistsBypassHttpCache.delete(limit);
  const promise = apiCall(
    `${API_CONFIG.endpoints.artists}?limit=${limit}`,
    bypass ? { cache: 'reload' } : {}
  )
    .then((data) => {
      _artistsCache.set(limit, { data, at: Date.now() });
      _artistsInFlight.delete(limit);
      return data;
    })
    .catch((error) => {
      _artistsInFlight.delete(limit);
      throw error;
    });

  _artistsInFlight.set(limit, promise);
  return promise;
};
// Same cache/in-flight-dedup shape as getArtists above, but for the
// heatmap's lightweight endpoint - which returns every non-graduated tracked
// artist trimmed to location/link fields only, instead of the top-N roster
// with full downsampled history getArtists(5000) was previously (ab)used for.
let _heatmapCache = null; // { data, at }
let _heatmapInFlight = null;

export const getHeatmapArtists = () => {
  if (_heatmapCache && (Date.now() - _heatmapCache.at) < ARTISTS_CACHE_TTL_MS) {
    return Promise.resolve(_heatmapCache.data);
  }
  if (_heatmapInFlight) return _heatmapInFlight;

  const bypass = _heatmapBypassHttpCache;
  _heatmapBypassHttpCache = false;
  _heatmapInFlight = apiCall(API_CONFIG.endpoints.heatmap, bypass ? { cache: 'reload' } : {})
    .then((data) => {
      _heatmapCache = { data, at: Date.now() };
      _heatmapInFlight = null;
      return data;
    })
    .catch((error) => {
      _heatmapInFlight = null;
      throw error;
    });

  return _heatmapInFlight;
};
// Same cache/in-flight-dedup shape as getHeatmapArtists above. Backs
// genreVotesCache.js's fetchGenreVotesRaw(), which used to read the
// genreVotes Firestore collection directly from the browser (a full
// collection scan per tab); this now hits Backend/routes/artists.py's own
// in-process-cached aggregate instead, so many tabs share one Firestore
// read per TTL window rather than each doing their own scan.
let _genreVotesCache = null; // { data, at }
let _genreVotesInFlight = null;

export const getGenreVotes = () => {
  if (_genreVotesCache && (Date.now() - _genreVotesCache.at) < ARTISTS_CACHE_TTL_MS) {
    return Promise.resolve(_genreVotesCache.data);
  }
  if (_genreVotesInFlight) return _genreVotesInFlight;

  _genreVotesInFlight = apiCall(API_CONFIG.endpoints.genreVotes)
    .then((data) => {
      _genreVotesCache = { data, at: Date.now() };
      _genreVotesInFlight = null;
      return data;
    })
    .catch((error) => {
      _genreVotesInFlight = null;
      throw error;
    });

  return _genreVotesInFlight;
};
// After an admin merge/cleanup, so the next read sees the change (the backend
// drops its own cache in the same request).
export const clearGenreVotesCache = () => { _genreVotesCache = null; };
export const triggerUpdate = () => apiCall(API_CONFIG.endpoints.updateNow, { method: 'POST' });
// `skip`: sources (upload/deezer/youtube/featured) that failed to play here;
// the backend drops its cached copies and continues the chain past them.
export const getArtistTopTrack = ({ artistId, spotifyId, artistName, soundcloudUrl, soundcloudId, youtubeId, deezerUrl, skip } = {}) => {
  const params = new URLSearchParams();
  if (artistId) params.set('artist_id', artistId);
  if (spotifyId) params.set('spotify_id', spotifyId);
  if (artistName) params.set('artist_name', artistName);
  if (soundcloudUrl) params.set('soundcloud_url', soundcloudUrl);
  if (soundcloudId) params.set('soundcloud_id', soundcloudId);
  if (youtubeId) params.set('youtube_id', youtubeId);
  if (deezerUrl) params.set('deezer_url', deezerUrl);
  if (skip && skip.length) params.set('skip', skip.join(','));
  return apiCall(`${API_CONFIG.endpoints.artistTopTrack}?${params.toString()}`);
};
// Motion history for the /compare page, by artist id - includes artists who
// have graduated off the board (which getArtists never returns).
export const getCompareArtists = (ids) =>
  apiCall(`${API_CONFIG.endpoints.compareArtists}?ids=${ids.map(encodeURIComponent).join(',')}`);
export const getArtistSample = (artistId) =>
  apiCall(`${API_CONFIG.endpoints.artistSample}?artist_id=${encodeURIComponent(artistId)}`);
export const getFanProfile = (userId) =>
  apiCall(`${API_CONFIG.endpoints.fanProfile}?user_id=${encodeURIComponent(userId)}`);
export const getArtistSavedArtists = (userId) =>
  apiCall(`${API_CONFIG.endpoints.artistSavedArtists}?user_id=${encodeURIComponent(userId)}`);
// Fire-and-forget outbound-click counter for the /go/:artistKey hub. keepalive
// lets the request survive the navigation to the external platform.
export const recordLinkClick = (artistId, platform) =>
  fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/link-click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform }),
    keepalive: true,
  }).catch(() => {});
// Cached, lightweight avatar + co-sign count for the public /go hub (the full
// single-artist route is too heavy for viral bio-link traffic).
export const getArtistHubExtras = (artistId) =>
  apiCall(`${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/hub-extras`);
export const getSingleArtist = (artistId) =>
  apiCall(`${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}`);
// Signed-in only (backend 401s without a valid token) - the artist profile
// page's gated Soundcharts section. Not run through apiCall()'s cache since
// it needs a per-call Authorization header.
export const getArtistSoundcharts = (artistId, token) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/soundcharts`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    return res.json();
  });
export const getTopFans = (limit = 10) =>
  apiCall(`${API_CONFIG.endpoints.topFans}?limit=${limit}`);
export const getHallOfFame = () =>
  apiCall(API_CONFIG.endpoints.hallOfFame);

export const getAlreadyMainstream = () =>
  apiCall(API_CONFIG.endpoints.alreadyMainstream);

export const getVeterans = () =>
  apiCall(API_CONFIG.endpoints.veterans);

// Admin-only (backend enforces via @require_admin) - not run through
// apiCall()'s cache since it needs a per-call Authorization header, same
// as getArtistSoundcharts above.
export const getBadgeConfig = (token) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.badgeConfig}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    return res.json();
  });

// Admin-only (backend @require_admin): economy health - Scout Score earned,
// spent and unspent, and the co-sign breakout ratio. Per-call Authorization header, no caching.
export const getEconomy = (token) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.economy}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    return res.json();
  });

export const updateBadgeConfig = (token, foundingMemberCutoff) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.badgeConfig}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ foundingMemberCutoff }),
      signal,
    });
    return res.json();
  });

// Signed-in: co-sign (add a bookmark) through the gated endpoint that enforces
// the free co-sign allowance. Returns the raw Response so callers can branch on
// res.status: 429 = free co-signs used up (body has `cost` and `balance`),
// 402 = paying but not enough Scout Score. `{ pay: true }` spends Scout Score
// once the free ones are gone - use cosignWithPay.js rather than calling this
// directly. Removing goes through removeCosign.
export const addCosign = (token, artist, { pay = false } = {}) =>
  runWithTimeout((signal) => fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.cosign}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(pay ? { artist, pay: true } : { artist }),
    signal,
  }));

// POSTs JSON with the caller's token and returns { ok, status, ...body }.
const postAuthed = (endpoint, token, payload) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
      signal,
    });
    const body = await res.json().catch(() => ({}));
    return { ...body, ok: res.ok, status: res.status };
  });

// Signed-in: un-co-sign an artist. Server-side, like adding - firestore.rules
// keeps the co-sign lists out of client writes. Resolves to { ok, status, ... }.
export const removeCosign = (token, artistKey) =>
  postAuthed(API_CONFIG.endpoints.cosignRemove, token, { artistKey });

// Signed-in: save the private note on one of your co-signs. Server-side, since
// the browser can't write the co-sign list (firestore.rules).
export const saveCosignNote = (token, artistKey, note) =>
  postAuthed(API_CONFIG.endpoints.cosignNote, token, { artistKey, note });

// Signed-in: your own Scout Score wallet - { scoutScore, spent, balance,
// costs: { extraCosign, livery }, ownedLiveries, cosigns: { welcomeRemaining,
// freeLeftToday } }. Rejects on a non-2xx.
export const getScoutWallet = (token) =>
  runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.scoutWallet}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
    return body;
  });

// Signed-in: spend Scout Score to unlock a livery for good (free if already
// owned). status 402 = not enough; the body has `cost` and `balance`. On
// success the body is the updated wallet.
export const unlockLivery = (token, livery) =>
  postAuthed(API_CONFIG.endpoints.liveryUnlock, token, { livery });

// Signed-in: put an owned livery on your own (fan) profile. 402 = not owned.
export const setLivery = (token, livery) =>
  postAuthed(API_CONFIG.endpoints.livery, token, { livery });

// Signed-in only: a Followers or Following list. Pass `{ userId }` for an
// account or `{ artistKey }` for an artist page. Rejects on a non-2xx.
export const getConnections = async (token, type, { userId, artistKey }) => {
  const params = new URLSearchParams({ type });
  if (userId) params.set('user_id', userId);
  if (artistKey) params.set('artist_key', artistKey);
  return runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.connections}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.message || `HTTP error! status: ${res.status}`);
    return json;
  });
};

// Live shows on artist profiles. The public list is a plain GET (cached for
// 60s server-side); the owner/admin calls need a per-call Authorization
// header and resolve to { ok, status, data } so callers can show the
// backend's validation message (e.g. "Date can't be in the past").
export const getArtistShows = (artistId) =>
  apiCall(`${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/shows`);

const showsRequest = (token, path, method = 'GET', body) => runWithTimeout(async (signal) => {
  const res = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
});
const showsPath = (artistId) => `${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/shows`;

export const getManageableShows = (token, artistId) =>
  showsRequest(token, `${showsPath(artistId)}/manage`);
export const createArtistShow = (token, artistId, show) =>
  showsRequest(token, showsPath(artistId), 'POST', show);
export const updateArtistShow = (token, artistId, showId, fields) =>
  showsRequest(token, `${showsPath(artistId)}/${encodeURIComponent(showId)}`, 'PATCH', fields);
export const deleteArtistShow = (token, artistId, showId) =>
  showsRequest(token, `${showsPath(artistId)}/${encodeURIComponent(showId)}`, 'DELETE');

// A signed-in fan flags a public show: reason is one of wrong_artist,
// cancelled, wrong_details.
export const reportShow = (token, showId, reason) =>
  showsRequest(token, `/api/shows/${encodeURIComponent(showId)}/report`, 'POST', { reason });
// A report about an artist's show list as a whole (wrong_artist /
// wrong_details / missing); admins see and clear these on /admin/shows.
export const reportShowList = (token, artistId, reason) =>
  showsRequest(token, `/api/artists/${encodeURIComponent(artistId)}/shows/report`, 'POST', { reason });
export const adminClearShowListReports = (token, artistId) =>
  showsRequest(token, `/api/admin/shows/list-reports/${encodeURIComponent(artistId)}`, 'DELETE');

// Admin-only (backend @require_admin): the shows review queue (uncertain
// imports + reported shows), decisions on a queued show, and running the
// import on demand (one artist, or everyone in the background).
export const getShowsReviewQueue = (token) => showsRequest(token, '/api/admin/shows/review');
export const adminUpdateShow = (token, showId, fields) =>
  showsRequest(token, `/api/admin/shows/${encodeURIComponent(showId)}`, 'PATCH', fields);
export const runShowsImport = (token, artistId) =>
  showsRequest(token, '/api/admin/shows/import', 'POST', artistId ? { artist_id: artistId } : {});

// Admin-only: the Act types queue (artists with no act type, unread
// "Something else" notes, coverage) and setting types / marking a note read.
export const getActTypesQueue = (token, offset = 0) =>
  showsRequest(token, `/api/admin/act-types${offset ? `?offset=${offset}` : ''}`);
export const adminSetActTypes = (token, artistId, body) =>
  showsRequest(token, `/api/admin/artists/${encodeURIComponent(artistId)}/act-types`, 'POST', body);

// Admin-only: one-click check of whether our Soundcharts plan includes
// artist events (read-only). artistId optional - blank tries several artists.
export const runSoundchartsEventsCheck = (token, artistId) =>
  showsRequest(token, '/api/admin/shows/soundcharts-check', 'POST', artistId ? { artist_id: artistId } : {});

// "Paste a link to your show": read show details from a link to pre-fill the
// form (nothing saved), and manage pages re-checked by the weekly import.
export const resolveShowLink = (token, artistId, url) =>
  showsRequest(token, `${showsPath(artistId)}/resolve-link`, 'POST', { url });
const feedsPath = (artistId) => `${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/show-feeds`;
export const addShowFeed = (token, artistId, url) => showsRequest(token, feedsPath(artistId), 'POST', { url });
export const removeShowFeed = (token, artistId, url) =>
  showsRequest(token, `${feedsPath(artistId)}?url=${encodeURIComponent(url)}`, 'DELETE');

// ---- link pages (nexxup.link) ----
// Each resolves to { ok, status, data } so the editor can branch on 409
// (page changed elsewhere) and 400 (validation message) without try/catch.
function linkRequest(path, { method = 'GET', token, body, form } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body) headers['Content-Type'] = 'application/json';
  return runWithTimeout(async (signal) => {
    const res = await fetch(`${API_CONFIG.baseURL}${path}`, {
      method,
      headers,
      body: form || (body ? JSON.stringify(body) : undefined),
      signal,
    });
    let data = {};
    try { data = await res.json(); } catch { /* empty or non-JSON body */ }
    return { ok: res.ok, status: res.status, data };
  });
}
const linkPagePath = (artistId) => `${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/link-page`;
// Public, uncached: the artist, their saved page (null until first save) and upcoming shows.
export const getLinkPage = (artistId) => linkRequest(linkPagePath(artistId));
// base_updated_at is the version the editor loaded; a 409 means another tab or device saved since.
export const saveLinkPage = (token, artistId, page, baseUpdatedAt) =>
  linkRequest(linkPagePath(artistId), { method: 'PUT', token, body: { page, base_updated_at: baseUpdatedAt || null } });
export const checkLinkHandle = (token, handle) =>
  linkRequest(`/api/link-handles/${encodeURIComponent(handle)}`, { token });
export const claimLinkHandle = (token, artistId, handle) =>
  linkRequest(`${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/link-handle`, { method: 'PUT', token, body: { handle } });
export const uploadLinkPageImage = (token, artistId, file) => {
  const form = new FormData();
  form.append('file', file);
  return linkRequest(`${linkPagePath(artistId)}/images`, { method: 'POST', token, form });
};
// The verified owner's extra profile photos (the carousel after their account
// picture, up to 4 more). Each resolves to { ok, status, data: { photos } }.
const photosPath = (artistId) => `${API_CONFIG.endpoints.artists}/${encodeURIComponent(artistId)}/photos`;
export const addArtistPhoto = (token, artistId, file) => {
  const form = new FormData();
  form.append('file', file);
  return linkRequest(photosPath(artistId), { method: 'POST', token, form });
};
export const removeArtistPhoto = (token, artistId, url) =>
  linkRequest(photosPath(artistId), { method: 'DELETE', token, body: { url } });
export const reorderArtistPhotos = (token, artistId, photos) =>
  linkRequest(photosPath(artistId), { method: 'PUT', token, body: { photos } });
// Server-side preview of a pasted link: its real title and thumbnail.
export const unfurlLink = (token, url) =>
  linkRequest(`/api/unfurl?url=${encodeURIComponent(url)}`, { token });

export default API_CONFIG;

